/**
 * PublishFormVersionDialog — confirmation de publication d'un brouillon (1c-iii).
 *
 * Publication = intention métier unique : `POST /publish { changelog? }` (atomique
 * serveur). Le pré-check strict (schéma non vide) est fait par l'appelant AVANT
 * d'ouvrir ce dialogue (décision PO §7) ; ici on confirme + note de version
 * optionnelle. Avertissement d'immutabilité. Gère 422 vide / 409 déjà-publiée /
 * 409 archivée / réseau ; double-submit bloqué. Focus initial sur Annuler.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { usePublishFormVersion } from "../../../lib/queries/formTemplates";
import { toast } from "../../../lib/toast";

type Props = {
  readonly templateId: string;
  readonly versionId: string;
  readonly versionNumber: number;
  readonly onClose: () => void;
  readonly onPublished: () => void;
};

export function PublishFormVersionDialog({
  templateId,
  versionId,
  versionNumber,
  onClose,
  onPublished,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const changelogId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const [changelog, setChangelog] = useState("");

  const publish = usePublishFormVersion(versionId, templateId);
  const { reset: resetMutation } = publish;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !publish.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, publish.isPending]);

  const onConfirm = () => {
    if (publish.isPending) return;
    publish.mutate(changelog.trim() === "" ? null : changelog.trim(), {
      onSuccess: () => {
        toast.success(t("form_templates.publish.success", { version: versionNumber }));
        onPublished();
      },
    });
  };

  const apiErrorMsg =
    publish.error instanceof ApiException
      ? getApiErrorMessage(publish.error, lang) || t("errors.generic")
      : publish.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!publish.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("form_templates.publish.title", { version: versionNumber })}
          </h2>
        </header>

        <div className="space-y-3 px-5 py-5">
          <p className="text-sm text-amber-700">{t("form_templates.publish.immutable_warning")}</p>

          <div>
            <label htmlFor={changelogId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.publish.changelog_label")}
              <span className="ml-1 font-normal text-slate-400">
                {t("form_templates.fields.optional")}
              </span>
            </label>
            <textarea
              id={changelogId}
              value={changelog}
              rows={2}
              maxLength={2000}
              disabled={publish.isPending}
              onChange={(e) => setChangelog(e.target.value)}
              className="focus-visible:border-brand-500 focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={publish.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={publish.isPending}
            aria-busy={publish.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {publish.isPending
              ? t("form_templates.publish.submitting")
              : t("form_templates.publish.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
